import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, CheckCircle2, ChevronDown, Circle, CircleDot, LayoutGrid } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useRoutines, type WebRoutine } from '../../../../lib/routines';
import { queryKeys } from '../../../../lib/query-client';
import { fetchConnectors } from '../../../../lib/connectors';
import {
  BAND_CONNECT_POLL_INTERVAL_MS,
  bandAppEffectiveStatus,
  bandAppRows,
  bandCardSeenFields,
  bandConnectUrlFrom,
  bandRowExpandable,
  bandAppSplit,
  connectedToolkitsOf,
  isBandAppConnectable,
  isBandAppPending,
  shouldPollBandConnect,
  type BandCardAppData,
  type BandCardAppStatus,
  type BandCardData,
  type BandCardTaskStatusData,
} from '../../../../lib/band-cards';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { AgentJoinCard } from '../../AgentJoinCard';
import { Button, Card, Collapsible, Typography } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import { BandAgentLine, BandAppChips } from './BandAgentRows';
import { connectorRoutinesForSlugs } from '../../../../lib/routine-groups';
import { ConnectorRoutineRows } from './ConnectorRoutineRows';
import styles from './BandCard.module.css';

/**
 * The band once it exists, drawn on the turn the connect chain reports back — the whole team and every
 * app it runs on, under one reply.
 *
 * <h2>Why this exists at all: it REPLACES N stacked connector cards</h2>
 *
 * A band connects two to five apps in one chain, and the post-connected card draws one app expanded.
 * Four of those under one reply is a wall of switches nobody reads. So each app is a collapsed row here
 * — logo, name, where it stands, how many of its routines are running — and the rows that have routines
 * open to exactly the same switches the post-connected card draws (`ConnectorRoutineRows`, shared so the
 * two cards cannot drift).
 *
 * <h2>The rows are LIVE — including the statuses</h2>
 *
 * The routine counts and switches come from `useRoutines()`, filtered per app, for the same reason the
 * post-connected card reads them live: a switch drawn from the turn's snapshot shows the wrong state the
 * moment anything else moves it.
 *
 * The app STATUSES are live too, and that is what makes the card FINISH. The marker is stamped when the
 * chain is handed over, so every app in it reads `pending` forever — the owner connects four apps in the
 * browser, comes back, and the card still says "Not connected" beside a button asking them to do it
 * again. Every status read therefore goes through `bandAppEffectiveStatus` against the shared connectors
 * query, never `app.status` directly. A `skipped` or `failed` app is still a record of what the chain
 * did; only "not connected yet" is a claim that keeps moving.
 */
/**
 * Where one laid-out task stands. Decorative: the status is already in the reply's words and in the
 * plan's own shape, and a per-row label would turn a quiet list into three columns of state.
 */
function TaskStatusIcon({ status }: { status: BandCardTaskStatusData }) {
  const Icon = status === 'done' ? CheckCircle2 : status === 'working' ? CircleDot : Circle;
  return <Icon size={14} aria-hidden className={styles.taskStatus} data-status={status} />;
}

export function BandCard({ card, conversationId }: { card: BandCardData; conversationId?: string }) {
  const { t } = useTranslation();
  const { state } = useRoutines();
  const { logPageClickBi } = useGenericBi();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Set when the owner opens the connect chain, which is what ARMS the poll. Before that there is
  // nothing in flight to watch, and polling on every band card in the transcript would be a request per
  // card every few seconds for a chain nobody started.
  const [connectStartedAt, setConnectStartedAt] = useState<number | null>(null);

  const routines = state.kind === 'ready' ? state.routines : [];
  const widgetCount = card.homeWidgets?.length ?? 0;

  // The SAME connectors read the in-chat connector card and the Connectors screen share (one query key,
  // so the two can never disagree about whether an app is on). React Query owns the poll and the focus
  // refetch — the connect happens in another browser tab, so coming back is when the answer changed.
  const { data: connectors } = useQuery({
    queryKey: queryKeys.connectors,
    queryFn: fetchConnectors,
    refetchOnWindowFocus: connectStartedAt !== null,
    // Read off the FRESHEST payload every tick, so the loop stops the moment the last app lands rather
    // than a tick later. Bounded on purpose: an owner who abandons the browser tab must not leave a
    // request loop behind them, and the focus refetch catches a connect they finish after that.
    refetchInterval: (latest) => {
      if (connectStartedAt === null) return false;
      const live = connectedToolkitsOf(latest?.connectors);
      if (bandAppSplit(card.apps, card.connectUrl, live).cluster.every((app) => !isBandAppPending(bandAppEffectiveStatus(app, live)))) {
        return false;
      }
      return shouldPollBandConnect(connectStartedAt, Date.now()) ? BAND_CONNECT_POLL_INTERVAL_MS : false;
    },
  });

  const connectedToolkits = useMemo(() => connectedToolkitsOf(connectors?.connectors), [connectors]);
  // `cluster` is the chain's checklist, `settled` is everything it is not accounting for. Disjoint by
  // construction: an app drawn as a chip AND as a row below says the same thing twice in two treatments,
  // which reads as two different apps. Pure and shared with both phones — see `lib/band-cards.ts`.
  const { cluster, settled } = useMemo(
    () => bandAppSplit(card.apps, card.connectUrl, connectedToolkits),
    [card.apps, card.connectUrl, connectedToolkits],
  );
  const pendingCount = cluster.filter((app) => isBandAppPending(bandAppEffectiveStatus(app, connectedToolkits))).length;
  // Scoped to the CLUSTER rather than the whole band: an app the owner skipped is not work still to do,
  // so counting it in the denominator makes a finished chain read as permanently incomplete.
  const connectedCount = cluster.length - pendingCount;
  // "Connect all" is wrong for ONE app, and one app left is the common end of a half-finished chain.
  // With none left it stops being an invitation at all and reports the outcome instead — reusing the
  // label every connector row already carries rather than minting a string for 37 locales to translate.
  const connectActionLabel = t(pendingCount === 0 ? 'common.connected' : pendingCount === 1 ? 'common.connect' : 'band.card.connectAll');

  // Every routine standing behind this band's apps, for the impression. Same rule the rows use, so the
  // card's own numbers and what a reader can open cannot disagree.
  const cardRoutineNames = useMemo(
    () =>
      connectorRoutinesForSlugs(routines, card.apps.flatMap((app) => [app.toolkit, app.group])).map(
        (routine) => routine.name ?? routine.id,
      ),
    [routines, card.apps],
  );
  const seenRef = useItemSeenBi('band_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: bandCardSeenFields(card, cardRoutineNames),
  });

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={t('band.card.accessibilityLabel')} data-testid="chat-band-card">
      <Typography variant="h3" className={styles.name}>
        {card.name}
      </Typography>
      {/* The band arriving IS the product's signature moment, so it gets the same orbit the "joined your
          team" card plays rather than a line of dots: the whole band flies into the constellation at
          once. A band is several hires in one turn, which is exactly the shape that card was built for —
          and `showExistingTeam={false}` is web's spelling of native's empty `existingAgents`, because
          this card is about THIS group landing, not about where they sit in a wider roster.

          `stone` rather than the join card's own white: on web a card nested in a card takes the recessed
          skin (the DS Cards rule), where native draws both at `backgroundElevated`. */}
      <AgentJoinCard agents={card.agents} variant="stone" showExistingTeam={false} embedded className={styles.orbit} />
      {/* The names still have to be READABLE — the orbit is the celebration and its nodes carry no
          labels, so without this the owner watches four anonymous dots and never learns who joined. No
          dots on this line: the orbit is already drawing them, and twice is the same marks twice. */}
      <BandAgentLine agents={card.agents} showDots={false} />
      {/* The goal, and the work under it, drawn as a PLAN rather than as progress. Nothing here is
          running: the band was a pick, and the reply beside this card is asking for the go that sets it
          off. So each task shows who it is on and where it stands, and on this first card that is idle
          for every one of them — which is the point. The same section treatment and label key as the
          offer card, because the reader is looking at the goal they chose a band for. */}
      {card.goal && (
        <div className={styles.section}>
          <Typography variant="h4" className={styles.sectionLabel}>
            {t('band.offerCard.goal')}
          </Typography>
          <Typography variant="p2">{card.goal.title}</Typography>
          {card.goal.tasks && card.goal.tasks.length > 0 && (
            <ul className={styles.tasks}>
              {card.goal.tasks.map((task, index) => (
                <li key={`${task.title}-${index}`} className={styles.task}>
                  <TaskStatusIcon status={task.status} />
                  <Typography variant="p2" className={styles.taskTitle}>
                    {task.title}
                  </Typography>
                  {task.agent && (
                    <Typography variant="p3" className={styles.taskAgent}>
                      {task.agent}
                    </Typography>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {widgetCount > 0 && (
        <div className={styles.section}>
          <Typography variant="h4" className={styles.sectionLabel}>
            {t('connectors.card.homePageSection')}
          </Typography>
          <div className={styles.widgetLine}>
            <LayoutGrid size={16} aria-hidden className={styles.widgetIcon} />
            <Typography variant="p2">{t(`packs.count.homeCards.${widgetCount === 1 ? 'one' : 'other'}`, { count: widgetCount })}</Typography>
          </div>
        </div>
      )}
      {card.apps.length > 0 && (
        <div className={styles.section}>
          {/* "Apps", not the nav's word: a first-time owner calls these apps, and a card heading is not
              a navigation label. The same key the offer card uses, so the two band cards cannot name the
              same row differently. */}
          <Typography variant="h4" className={styles.sectionLabel}>
            {t('band.offerCard.apps')}
          </Typography>
          {/* The apps the chain is walking collapse into ONE cluster: named chips and a single action
              that opens the chain. They used to be a full-width row each, all reading "Not connected" —
              three lines of the same sentence, with the actual affordance on a second card below asking
              for the same apps again. */}
          {cluster.length > 0 && card.connectUrl && (
            <div className={styles.pending}>
              {/* Every app the band asked for, NAMED — the ones already connected keep their check here
                  rather than disappearing mid-chain, so the cluster reads as a checklist filling in
                  instead of a shrinking row of anonymous plates. */}
              <BandAppChips
                apps={cluster}
                ariaLabel={t('band.offerCard.apps')}
                isConnected={(app) => bandAppEffectiveStatus(app, connectedToolkits) === 'connected'}
              />
              <div className={styles.pendingAction}>
                {/* Only once something has landed: "0 of 4 connected" on first sight is a progress line
                    for work the owner has not been asked to start yet. */}
                {connectedCount > 0 && (
                  <Typography variant="p3" color="secondary" className={styles.progress}>
                    {t('band.card.connectProgress', { done: connectedCount, total: cluster.length })}
                  </Typography>
                )}
                {/* `external`, because the link is an absolute one on the harness host: without it the
                    Button rewrites the href with the active mount prefix and the button goes nowhere.
                    The label names the apps to a screen reader — the chips are separate elements, so the
                    button's own label is the only thing announced with it.

                    "Connect all" is wrong for one app, and one app left is the common end of a chain the
                    owner half-finished. */}
                {/* Nothing left to connect: the chain's card KEEPS its shape (it is not the completion
                    card, and turning into one is what drew the same band twice), and the action becomes
                    the statement that the work is done. Disabled rather than removed — the button is
                    where the reader last acted, so saying "Connected" there is the answer to what they
                    did; an empty space is not. */}
                <Button
                  size="small"
                  className={styles.connectAll}
                  disabled={pendingCount === 0}
                  aria-label={`${connectActionLabel}: ${cluster
                    .filter((app) => isBandAppPending(bandAppEffectiveStatus(app, connectedToolkits)))
                    .map((app) => app.name)
                    .join(', ')}`}
                  onClick={() => {
                    logPageClickBi(
                      { ...CHAT_HOME_BI, button_name: 'band-card-connect-all', ...(conversationId ? { conversation_id: conversationId } : {}) },
                      {
                        additionalFields: {
                          band: card.name,
                          // WHICH connectors this opens, not just how many — the point of the click is the
                          // set it is about to walk.
                          connectors: cluster
                            .filter((app) => isBandAppPending(bandAppEffectiveStatus(app, connectedToolkits)))
                            .map((app) => app.toolkit)
                            .join(','),
                          pending: String(pendingCount),
                        },
                      },
                    );
                    // Arms the poll — nothing above watches the connectors list until this happens.
                    setConnectStartedAt(Date.now());
                    // `window.open`, NOT an <a target="_blank">, because the chain's finish page closes
                    // the tab when it is done: a browser lets only a window that SCRIPT opened close
                    // itself, and a link-opened one is refused. Opened from inside the click handler, so
                    // it is a user gesture and no popup blocker applies. `noopener` would sever the same
                    // relationship the close depends on, so it is deliberately absent here — the page
                    // being opened is our own.
                    if (card.connectUrl) {
                      window.open(bandConnectUrlFrom(card.connectUrl, 'web'), '_blank');
                    }
                  }}
                >
                  {connectActionLabel}
                </Button>
              </div>
            </div>
          )}
          {settled.length > 0 && (
            <ul className={styles.apps}>
              {bandAppRows(settled, connectedToolkits).map((row) => (
                <BandAppRow
                  key={row.key}
                  apps={row.apps}
                  routines={routines}
                  settled={pendingCount === 0}
                  status={bandAppEffectiveStatus(row.apps[0], connectedToolkits)}
                  open={Boolean(expanded[row.key])}
                  conversationId={conversationId}
                  band={card.name}
                  onToggle={() => setExpanded((current) => ({ ...current, [row.key]: !current[row.key] }))}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * One row of the app list: a single app, or a fully-connected group of them drawn together (see
 * `bandAppRows`). A group's row shows every plate, the names joined, and the routines of all of them —
 * which is exactly what its members' separate rows showed, minus the repetition.
 */
function BandAppRow({
  apps,
  routines,
  settled,
  status,
  open,
  conversationId,
  band,
  onToggle,
}: {
  apps: BandCardAppData[];
  routines: WebRoutine[];
  /** For the row's own click events — the conversation it happened in, and which band it belongs to. */
  conversationId?: string;
  band: string;
  /** True once nothing on the card is still connecting. While the chain is running the rows say only
   *  where each app stands — a routine count beside an app that is mid-connect is a second number
   *  competing with the one the owner is actually waiting on, about work that has not started. */
  settled: boolean;
  /** The row's LIVE standing, resolved by the card through `bandAppEffectiveStatus`. A row must never
   *  read `app.status` itself — an app the marker calls pending but the connectors list calls connected
   *  has routines behind it, so its row has to open. */
  status: BandCardAppStatus | undefined;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const app = apps[0];
  // Every spelling this row's apps answer to — each toolkit AND the group key they fold under, because a
  // routine is filed by its best-practices FOLDER and social's folder is the group, not the app.
  const rows = useMemo(
    () => connectorRoutinesForSlugs(routines, apps.flatMap((member) => [member.toolkit, member.group])),
    [routines, apps],
  );
  // An app that was skipped or failed has nothing behind it: no routines were installed, so a count and
  // an expander would both point at an empty panel. A group only ever folds when every member connected,
  // so the first member's standing is the row's.
  const connectable = isBandAppConnectable({ ...app, status });
  // The row's own click, which had no event at all: opening an app to see what it actually runs is the
  // one interaction on this card that says the reader cares about the routines behind a connect.
  const connectors = apps.map((member) => member.toolkit).join(',');
  const logRowClick = (buttonName: string, fields: Record<string, string | undefined>) =>
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: buttonName, ...(conversationId ? { conversation_id: conversationId } : {}) },
      { additionalFields: { band, connectors, ...fields } },
    );
  // "n routines ON" — the RUNNING ones, not every row behind the chevron. A paused routine is an offer,
  // and counting it would tell the owner work is happening that is not. Same rule on both phones.
  const runningCount = connectable ? rows.filter((routine) => !routine.paused).length : 0;
  // One value gates the chevron AND the count line below — see `bandRowExpandable`, shared with both phones.
  const expandable = bandRowExpandable(connectable, rows.length, !settled);

  const head = (
    <>
      {/* The synthetic status the logo component reads — it takes a ConnectorStatus, and the card carries
          the two fields that matter. The app's real standing is the word beside it, not the plate. */}
      <span className={styles.appLogos}>
        {/* Two at most: past that, more plates say nothing a reader can act on and only push the name
            further right. Mirrors both phones. */}
        {apps.slice(0, 2).map((member) => (
          <ConnectorLogo
            key={member.toolkit}
            connector={{ toolkit: member.toolkit, logo: member.logoUrl, connected: connectable }}
            name={member.name}
            size={28}
          />
        ))}
      </span>
      <Typography variant="p2" className={styles.appName}>
        {apps.map((member) => member.name).join(', ')}
      </Typography>
      <span className={styles.appMeta}>
        {status && (
          // A connect that WORKED should read as good news, in the same green check the chain's chips
          // use — so the two halves of the card agree about what "connected" looks like. Every OTHER
          // status stays deliberately neutral: the owner chose to skip, and a failure is a retry the
          // agent raises in its own words, not a red row.
          <Typography variant="p3" color="secondary" className={styles.appStatus}>
            {status === 'connected' && <Check size={12} strokeWidth={2.5} absoluteStrokeWidth aria-hidden className={styles.statusCheck} />}
            {t(`band.card.status.${status}`)}
          </Typography>
        )}
        {expandable && (
          <Typography variant="p3" color="secondary" className={styles.appRoutines}>
            {t(`band.card.routines.${runningCount === 1 ? 'one' : 'other'}`, { count: runningCount })}
          </Typography>
        )}
        {expandable && <ChevronDown size={16} aria-hidden className={[styles.chevron, open ? styles.chevronOpen : ''].filter(Boolean).join(' ')} />}
      </span>
    </>
  );

  return (
    <li className={styles.app}>
      {expandable ? (
        // A disclosure, so the accessible name stays the row's own words and `aria-expanded` carries the
        // state; the expand/collapse wording is the hover hint rather than a label that would replace the
        // app's name for a screen reader.
        <Card
          component="button"
          variant="stone"
          size="sm"
          interactive
          className={styles.appHead}
          onClick={() => {
            // The state being MOVED TO, so the event says what the reader asked for rather than what was
            // on screen before they asked.
            logRowClick('band-card-app', { state: open ? 'collapsed' : 'expanded', routines: String(rows.length) });
            onToggle();
          }}
          aria-expanded={open}
          title={open ? t('band.card.collapse') : t('band.card.expand')}
        >
          {head}
        </Card>
      ) : (
        <Card variant="stone" size="sm" className={styles.appHead}>
          {head}
        </Card>
      )}
      {expandable && (
        <Collapsible open={open} innerClassName={styles.routines}>
          <ConnectorRoutineRows
            routines={rows}
            onToggleReported={(routine, paused) =>
              logRowClick('band-card-routine', { routine: routine.name ?? routine.id, state: paused ? 'off' : 'on' })
            }
          />
        </Collapsible>
      )}
    </li>
  );
}
